import {createBrowserRouter, useParams, Navigate} from 'react-router-dom';
import React, {lazy, Suspense} from 'react';
import ProtectedRoute from './ProtectedRoute';
import RootLayout from './layout/RootLayout';
import {useSelector} from 'react-redux';
import {loginLoader} from './pages/login/signIn/Login.loader';
import {suspendedConfirmationLoader} from './pages/suspendedConfirmation/suspendedConfirmation.loader';
import {initAxios} from './library/axios';
import {RootState} from './redux/store.type';
import {DELIVERY_TYPES, MESSAGE_TYPES} from './pages/branding/menu/brandingConstants';
import Page404 from './pages/page404/Page404';
import {eTicketLoader} from './pages/eTicket/ETicket.loader';
import {getDefaultPath} from './utils/routerUtils';
import ETicket from './pages/eTicket/ETicket';
import {isETicketDomain} from './config/config';
import TickchakLoader from './component/tickchakLoader/TickchakLoader';

// Lazy load everything the public eTicket page doesn't need, so the eTicket
// domain only downloads main.js. ETicket itself stays static (critical path).
const Login = lazy(() => import('./pages/login/signIn/Login'));
const SignUp = lazy(() => import('./pages/login/signUp/SignUp'));
const ViewSymbols = lazy(() => import('./component/ViewSymbols'));
const Layout = lazy(() => import('./layout/Layout'));
const VerifyScreen = lazy(() => import('./pages/package/componnent/verifyScreen/VerifyScreen'));
const RouteComponent = lazy(() => import('./components/RouteComponent/RouteComponent'));
const SuspendedConfirmation = lazy(() => import('./pages/suspendedConfirmation/suspendedConfirmation'));
const ReactShareDemo = lazy(() => import('./pages/reactShareDemo/ReactShareDemo'));
const ReactShareDemoNew = lazy(() => import('./pages/reactShareDemoNew/ReactShareDemoNew'));
const SalesCrmFableApp = lazy(() => import('./pages/salesCrmFable/SalesCrmFableApp'));
const SalesCrmAstra = lazy(() => import('./pages/salesCrmAstra/SalesCrmAstra'));

// Lazy load appPlus modules (including Mantine)
const MantineWrapper = lazy(() => import('./pages/appPlus/MantineWrapper'));
const AppPlusLayout = lazy(() => import('./pages/appPlus/layout/AppPlusLayout'));

const withSuspense = (el: React.ReactNode) => (
    <Suspense fallback={<TickchakLoader showClass="show" cssStyle="position:fixed;" />}>{el}</Suspense>
);

// Helper components for redirects - defined before router
const PidRedirect = () => {
    const {pid} = useParams();
    return <Navigate to={`/p/${pid}/home`} replace />;
};

const EidRedirect = () => {
    const {eid} = useParams();
    return <Navigate to={`/e/${eid}/realtime`} replace />;
};

const BrandingRedirect = () => {
    const {eid, page} = useParams();
    return <Navigate to={`/e/${eid}/${page}/branding/${MESSAGE_TYPES.sendTicket}/${DELIVERY_TYPES.whatsapp}`} replace />;
};

const BrandingMessageTypeRedirect = () => {
    const {eid, page, messageType} = useParams();
    return <Navigate to={`/e/${eid}/${page}/branding/${messageType}/${DELIVERY_TYPES.whatsapp}`} replace />;
};

const RootRedirect = () => {
    const {defaultPid, defaultEid} = useSelector((state: RootState) => state.user);
    return <Navigate to={getDefaultPath(defaultPid, defaultEid)} replace />;
};

initAxios();

const eTicketRoute = {element: <ETicket />, loader: eTicketLoader};
const page404Route = {path: '*', element: <Page404 eidParam="" pidParam="" />};

// Create the router with data router capabilities
export const router = createBrowserRouter([
    {
        path: '/',
        element: <RootLayout />,
        children: isETicketDomain ? [
            {path: 'n/:token', ...eTicketRoute},
            {path: ':token', ...eTicketRoute},
            page404Route,
        ] : [
            {index: true, element: <RootRedirect />},
            {path: 'login/forgetPassword', element: withSuspense(<Login pageType="resetPassword" />)},
            {path: 'login', element: withSuspense(<Login />), loader: loginLoader},
            {path: 'signUp', element: withSuspense(<SignUp />)},
            {path: 'viewSymbols', element: withSuspense(<ViewSymbols />)},
            {path: 'suspendedConfirmation', element: withSuspense(<SuspendedConfirmation />), loader: suspendedConfirmationLoader},
            {path: 'n/:token', ...eTicketRoute},
            {path: 'ReactShareDemo/*', element: <ProtectedRoute>{withSuspense(<ReactShareDemo />)}</ProtectedRoute>},
            {path: 'ReactShareDemoNew/*', element: <ProtectedRoute>{withSuspense(<ReactShareDemoNew />)}</ProtectedRoute>},
            {path: 'salesCrmAstra/*', element: <ProtectedRoute>{withSuspense(<SalesCrmAstra />)}</ProtectedRoute>},
            {path: 'salesCrmFable/*', element: <ProtectedRoute>{withSuspense(<SalesCrmFableApp />)}</ProtectedRoute>},
            {path: 'p/:pid', element: <ProtectedRoute>{withSuspense(<Layout />)}</ProtectedRoute>,
                children: [
                    {index: true, element: <PidRedirect />},
                    {path: 'packageVerification', element: withSuspense(<VerifyScreen />)},
                    {path: ':page', element: withSuspense(<RouteComponent />)},
                    {path: ':page/:subPage', element: withSuspense(<RouteComponent />)},
                    {path: ':page/:subPage/:pageId', element: withSuspense(<RouteComponent />)},
                    {path: ':page/branding', element: <BrandingRedirect />},
                    {path: ':page/branding/:messageType', element: <BrandingMessageTypeRedirect />},
                    {path: ':page/:subPage/:messageType', element: withSuspense(<RouteComponent />)},
                    {path: ':page/:subPage/:messageType/:deliveryType', element: withSuspense(<RouteComponent />)}
                ]
            },
            {
                path: 'e/:eid', element: <ProtectedRoute>{withSuspense(<Layout />)}</ProtectedRoute>,
                children: [
                    {index: true, element: <EidRedirect />},
                    {path: ':page', element: withSuspense(<RouteComponent />)},
                    {path: ':page/:subPage', element: withSuspense(<RouteComponent />)},
                    {path: ':page/:subPage/:pageId', element: withSuspense(<RouteComponent />)},
                    {path: ':page/branding', element: <BrandingRedirect />},
                    {path: ':page/branding/:messageType', element: <BrandingMessageTypeRedirect />},
                    {path: ':page/:subPage/:messageType', element: withSuspense(<RouteComponent />)},
                    {path: ':page/:subPage/:messageType/:deliveryType', element: withSuspense(<RouteComponent />)}
                ]
            },
            {
                path: 'appPlus/*',
                element: (
                    <ProtectedRoute>
                        <Suspense fallback={<div style={{padding: '24px', textAlign: 'center'}}>טוען...</div>}>
                            <MantineWrapper>
                                <AppPlusLayout />
                            </MantineWrapper>
                        </Suspense>
                    </ProtectedRoute>
                )
            },
            page404Route,
        ],
    },
]);
